import React from "react";
import SelectionItem from "../../components/shared/selectionItem";
import { Reason, ReasonGroupTuple, Species } from "../../types";

export interface ReasonItemProps {
  reason: ReasonGroupTuple;
  /** The animals the treatment is for, eg "For: Dog, Cat". Only for restricted treatments. */
  animalsLabel?: string;
  onSelectReason: (reason?: Reason) => void;
}

/**
 * The label for a treatment that is only for certain animals, eg "For: Dog, Cat" (D9).
 * Customers choose the treatment before saying which animal they have, so this tells them
 * up front.
 *
 * Returns undefined when the treatment is for any animal: it has no species, or covers all
 * of the known species. Species that aren't known (eg when they failed to load) are left out.
 *
 * @param template - The "treatmentForAnimals" translation, with an {animals} placeholder
 */
export const getTreatmentAnimalsLabel = (
  reason: Reason | undefined,
  species: Species[],
  template: string | undefined
): string | undefined => {
  const speciesCodes = reason?.species_codes ?? [];
  const animals = species.filter(
    speciesItem => speciesCodes.includes(speciesItem.code) && !!speciesItem.label?.trim()
  );

  if (animals.length === 0 || species.every(s => speciesCodes.includes(s.code))) {
    return undefined;
  }

  const animalNames = animals.map(speciesItem => speciesItem.label!.trim()).join(", ");

  if (template?.includes("{animals}")) {
    return template.replace("{animals}", animalNames);
  }

  // A translation without the placeholder still shows the animals
  return template ? `${template} ${animalNames}` : animalNames;
};

export const ReasonItem = ({ reason, animalsLabel, onSelectReason }: ReasonItemProps) => (
  <SelectionItem
    onChange={() => {
      onSelectReason(reason.reason);
    }}
    name={reason.reason?.name ?? ""}
    value={reason.reason?.name ?? ""}
    labelTitle={reason.reason?.name ?? ""}
    labelSubTitle={reason.reasonGroup?.name}
    labelNote={animalsLabel}
    checked={false}
  />
);

export default ReasonItem;
